import { client as axios } from '../../axios/client';
import {
  GET_COMMENTS_REQUEST,
  GET_COMMENTS_SUCCESS,
  GET_COMMENTS_FAILURE,
  ADD_COMMENT_REQUEST,
  ADD_COMMENT_SUCCESS,
  ADD_COMMENT_FAILURE
} from './commentTypes';

/**
 * Obtiene los comentarios de un ticket
 * @param {string} ticketId - ID del ticket
 * @param {string} token - Token de autenticación
 */
export const getTicketComments = (ticketId, token) => async (dispatch) => {
  try {
    dispatch({ type: GET_COMMENTS_REQUEST });

    // Configurar headers con el token si está disponible
    const headers = token ? { Authorization: `Bearer ${token}` } : {};

    // Realizar la llamada a la API para obtener los comentarios del ticket
    const response = await axios.get(`/kanban/tickets/${ticketId}/comments`, { headers });

    // Procesar la respuesta
    const responseData = response.data || [];

    // Formatear los comentarios para mantener la estructura esperada por la aplicación
    const comments = Array.isArray(responseData) ? responseData.map(comment => ({
      id: comment.id || comment._id,
      author: comment.name_agent || comment.author || comment.agent_name || 'Agente',
      text: comment.text || comment.comment || '',
      date: comment.date || comment.created_at,
      columnName: comment.column_name || comment.current_column || ''
    })) : [];

    // Despachar acción de éxito
    dispatch({
      type: GET_COMMENTS_SUCCESS,
      payload: {
        ticketId,
        comments
      }
    });

    return comments;
  } catch (error) {
    dispatch({
      type: GET_COMMENTS_FAILURE,
      payload: {
        error: error.message || 'Error al obtener comentarios'
      }
    });
    throw error;
  }
};

/**
 * Agrega un comentario a un ticket
 * @param {string} ticketId - ID del ticket
 * @param {string} text - Texto del comentario
 * @param {string} columnName - Nombre de la columna donde está el ticket
 * @param {string} token - Token de autenticación
 */
export const addTicketComment = (ticketId, text, columnName, token) => async (dispatch, getState) => {
  try {
    // Adding comment to ticket
    dispatch({ type: ADD_COMMENT_REQUEST });

    // Obtener información del agente actual
    const currentAgent = getState().agent || {};

    // Obtener el nombre del agente para mostrar en la interfaz
    const authorName = currentAgent.name || currentAgent.display_name || 'Agente';

    // Configurar headers con el token si está disponible
    const headers = token ? { Authorization: `Bearer ${token}` } : {};

    // Preparar los parámetros para la solicitud
    const params = {
      id_agent: currentAgent._id || '',
      comment: text,
      current_column: columnName, // Agregar el nombre de la columna actual
      name_agent: authorName // Agregar el nombre del agente que comenta
    };

    // Realizar la llamada a la API para agregar el comentario
    const response = await axios.post(`/kanban/tickets/${ticketId}/comments`, null, {
      params,
      headers
    });

    // Procesar la respuesta
    const responseData = response.data || {};

    // Formatear el comentario para mantener la estructura esperada
    const newComment = {
      id: responseData.id || responseData._id || Date.now().toString(),
      author: responseData.name_agent || authorName,
      text,
      date: responseData.created_at,
      // Usar el valor de column_name de la respuesta si existe, de lo contrario usar el valor enviado
      columnName: responseData.column_name || responseData.current_column || columnName
    };

          // Comment created successfully

    // Despachar acción de éxito
    dispatch({
      type: ADD_COMMENT_SUCCESS,
      payload: {
        ticketId,
        comment: newComment
      }
    });

    return newComment;
  } catch (error) {
    console.error('addTicketComment - Error:', error);
    dispatch({
      type: ADD_COMMENT_FAILURE,
      payload: {
        error: error.message || 'Error al agregar comentario'
      }
    });
    throw error;
  }
};

/**
 * Agrega un comentario a un ticket con el nombre del usuario logueado
 * @param {Object} params - Parámetros para agregar el comentario
 * @param {string} params.ticketId - ID del ticket
 * @param {string} params.text - Texto del comentario
 * @param {string} params.columnName - Nombre de la columna donde está el ticket
 * @param {string} params.token - Token de autenticación
 * @param {string} params.userName - Nombre del usuario logueado
 */
export const addTicketCommentWithUserName = (params) => async (dispatch, getState) => {
  const { ticketId, text, columnName, token, userName } = params;

  try {
    dispatch({ type: ADD_COMMENT_REQUEST });

    // Obtener información del agente actual
    const currentAgent = getState().agent || {};

    // Usar el nombre del usuario logueado si está disponible, o el nombre del agente como respaldo
    const authorName = userName || currentAgent.name || currentAgent.display_name || 'Agente';

    // Configurar headers con el token si está disponible
    const headers = token ? { Authorization: `Bearer ${token}` } : {};

    // Preparar los parámetros para la solicitud
    const requestParams = {
      id_agent: currentAgent._id || '',
      comment: text,
      current_column: columnName, // Agregar el nombre de la columna actual
      name_agent: userName || authorName // Usar el nombre del usuario logueado
    };

    // Realizar la llamada a la API para agregar el comentario
    const response = await axios.post(`/kanban/tickets/${ticketId}/comments`, null, {
      params: requestParams,
      headers
    });

    // Procesar la respuesta
    const responseData = response.data || {};

    // Formatear el comentario para mantener la estructura esperada
    const newComment = {
      id: responseData.id || responseData._id || Date.now().toString(),
      author: userName || authorName, // Usar el nombre del usuario logueado
      text,
      date: responseData.created_at,
      columnName: responseData.column_name || responseData.current_column || columnName
    };

    // Despachar acción de éxito
    dispatch({
      type: ADD_COMMENT_SUCCESS,
      payload: {
        ticketId,
        comment: newComment
      }
    });

    return newComment;
  } catch (error) {
    console.error('addTicketCommentWithUserName - Error:', error);
    dispatch({
      type: ADD_COMMENT_FAILURE,
      payload: {
        error: error.message || 'Error al agregar comentario'
      }
    });
    throw error;
  }
};
